import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';
import { t } from 'i18n';

// ── Query keys ───────────────────────────────────────────────────────────────
export const warehouseKeys = {
  warehouses: ['warehouses'],
  warehouse: (id) => ['warehouses', id],
  overview: (id) => ['warehouses', id, 'overview'],
  quota: ['warehouses', 'quota'],
  projects: ['dbt-projects'],
  project: (id) => ['dbt-projects', id],
  runs: (id) => ['dbt-projects', id, 'runs'],
  readiness: (id) => ['dbt-projects', id, 'readiness'],
};

// Human phrase for a warehouse's active dependents, e.g.
// "2 active pipelines and 1 active dbt project". Returns '' when there are none.
export function describeDependents(dependents) {
  if (!dependents) return '';
  const parts = [];
  const p = dependents.active_pipelines || 0;
  const d = dependents.active_dbt_projects || 0;
  if (p) parts.push(t('common.activePipelines', { count: p }));
  if (d) parts.push(t('common.activeDbtProjects', { count: d }));
  return parts.join(t('common.listAnd'));
}

// ── Warehouse Services ────────────────────────────────────────────────────────
// Every row has a `kind`: "lakehouse" (the platform-managed one — no connection
// details at all) or "byo" (its settings under `connection`).
export function useWarehouses() {
  return useQuery({
    queryKey: warehouseKeys.warehouses,
    queryFn: () => apiClient.get('/api/v1/warehouse/warehouses/').then((r) => r.data),
  });
}

export function useWarehouse(id) {
  return useQuery({
    queryKey: warehouseKeys.warehouse(id),
    queryFn: () => apiClient.get(`/api/v1/warehouse/warehouses/${id}/`).then((r) => r.data),
    enabled: !!id,
  });
}

// Tables, freshness, 30 days of loads, storage, maintenance and direct-SQL
// status of one warehouse.
export function useWarehouseOverview(id) {
  return useQuery({
    queryKey: warehouseKeys.overview(id),
    queryFn: () =>
      apiClient.get(`/api/v1/warehouse/warehouses/${id}/overview/`).then((r) => r.data),
    enabled: !!id,
  });
}

// Plan-based quota for bring-your-own warehouses (the Lakehouse holds no slot):
// { plan, limit (null = unlimited), used, can_create }.
export function useWarehouseQuota() {
  return useQuery({
    queryKey: warehouseKeys.quota,
    queryFn: () => apiClient.get('/api/v1/warehouse/warehouses/quota/').then((r) => r.data),
  });
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/warehouse/warehouses/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: warehouseKeys.warehouses }),
  });
}

export function useUpdateWarehouse(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.patch(`/api/v1/warehouse/warehouses/${id}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: warehouseKeys.warehouses });
      qc.invalidateQueries({ queryKey: warehouseKeys.warehouse(id) });
    },
  });
}

// "Deletion" is a soft-disable (is_active=false): warehouses are referenced by
// dbt projects, pipelines and cubes, so the API never hard-deletes. Re-enabling
// is subject to the plan quota (may 400), so the single ['warehouses'] prefix
// invalidation below must keep covering the quota and detail queries.
export function useSetWarehouseActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }) =>
      apiClient
        .patch(`/api/v1/warehouse/warehouses/${id}/`, { is_active: isActive })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: warehouseKeys.warehouses }),
  });
}

// ── dbt Projects ─────────────────────────────────────────────────────────────
export function useDbtProjects() {
  return useQuery({
    queryKey: warehouseKeys.projects,
    queryFn: () => apiClient.get('/api/v1/dbt/dbt-projects/').then((r) => r.data),
    // The home summary and preparation list must follow runs that finish
    // while they stay open. Query's default pauses polling in the background.
    refetchInterval: 30000,
  });
}

export function useDbtProject(id) {
  return useQuery({
    queryKey: warehouseKeys.project(id),
    queryFn: () => apiClient.get(`/api/v1/dbt/dbt-projects/${id}/`).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 30000,
  });
}

// dbt projects are platform-managed: staff create and delete them, so the
// console deliberately has no create/delete mutations.
export function useUpdateDbtProject(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.patch(`/api/v1/dbt/dbt-projects/${id}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: warehouseKeys.projects });
      qc.invalidateQueries({ queryKey: warehouseKeys.project(id) });
    },
  });
}

export function useToggleDbtProject(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.patch(`/api/v1/dbt/dbt-projects/${id}/toggle/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: warehouseKeys.projects });
      qc.invalidateQueries({ queryKey: warehouseKeys.project(id) });
    },
  });
}

export function useTriggerDbtProject(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (command) =>
      apiClient
        .post(`/api/v1/dbt/dbt-projects/${id}/trigger/`, command ? { command } : {})
        .then((r) => r.data),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: warehouseKeys.runs(id) }), 2000);
    },
  });
}

// "Reset" on a node of the readiness graph: the planner forgets it was ever
// built and rebuilds it on its next tick, alone or with everything below it.
export function useResetDbtNodes(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ node, descendants }) =>
      apiClient
        .post(`/api/v1/dbt/dbt-projects/${projectId}/readiness/reset/`, { node, descendants: Boolean(descendants) })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: warehouseKeys.readiness(projectId) }),
  });
}

// The run history, polled every 5s. Run logs stay with support, never here.
export function useDbtRuns(projectId) {
  return useQuery({
    queryKey: warehouseKeys.runs(projectId),
    queryFn: () =>
      apiClient.get(`/api/v1/dbt/dbt-projects/${projectId}/runs/`).then((r) => r.data),
    enabled: !!projectId,
    refetchInterval: 5000,
  });
}

// The readiness planner's view of the project: what each node waits for, every
// raw table with its last landing, and the mismatches — computed read-only on
// the server. Polled at 30s; the planner itself ticks once a minute.
export function useDbtReadiness(projectId) {
  return useQuery({
    queryKey: warehouseKeys.readiness(projectId),
    queryFn: () =>
      apiClient.get(`/api/v1/dbt/dbt-projects/${projectId}/readiness/`).then((r) => r.data),
    enabled: !!projectId,
    refetchInterval: 30000,
  });
}
